export class UserRoles {
	public static readonly Admin = 'ADMIN';
	public static readonly User = 'USER';
	public static readonly ExternalPartner = 'EXTERNAL_PARTNER';
	public static readonly BrokerAdmin = 'BROKER_ADMIN';
	public static readonly BrokerUser = 'BROKER_USER';
	public static readonly BrokerAgent = 'BROKER_AGENT';
	public static readonly BUSINESS_CONSULTANT = 'BUSINESS_CONSULTANT';
	public static readonly BUSINESS_MANAGER = 'BUSINESS_MANAGER';
	public static readonly RENTER = 'RENTER';
	public static readonly BUSINESS_HEAD = 'BUSINESS_HEAD';
	/**
	 * Mesma matriz de `LESSOR_WAIVER` da API (ENG-568, DEC-23): quem pode abrir uma ocorrência
	 * Recorrente sem o bloco do Locador completo, informando motivo. Aqui só habilita o campo; a
	 * recusa de quem está fora é do servidor.
	 */
	public static readonly LessorWaivers = [
		UserRoles.Admin,
		UserRoles.User,
		UserRoles.BUSINESS_MANAGER,
		UserRoles.BUSINESS_HEAD,
	];
	/**
	 * Mesma matriz de `LESSOR_CONFERENCE` da API (ENG-568, M2): quem vê e marca a conferência do
	 * Jurídico nos documentos do Locador. O motivo interno da reprovação não é do parceiro.
	 */
	public static readonly LessorConference = [
		UserRoles.Admin,
		UserRoles.User,
		UserRoles.BUSINESS_MANAGER,
		UserRoles.BUSINESS_HEAD,
	];
	/**
	 * Mesma matriz de `BrokersRoles` da API: os papéis de quem pertence a um parceiro, e que portanto
	 * seguem o status dele (Pendente assinatura/Pendente bloqueiam a criação).
	 */
	public static readonly BrokersRoles = [
		UserRoles.BrokerAdmin,
		UserRoles.BrokerUser,
		UserRoles.BrokerAgent,
	];
	/** Mesma matriz de `CANCELLATION_REQUESTERS` da API. */
	public static readonly CancellationRequesters = [
		UserRoles.Admin,
		UserRoles.User,
		UserRoles.BrokerAdmin,
		UserRoles.BrokerUser,
		UserRoles.BrokerAgent,
	];
	/**
	 * Mesma matriz de `CANCELLATION_PARTNERS` da API: os papéis que veem o cancelamento pelo lado
	 * do parceiro. Nomeada porque estava escrita inline em dois lugares, e um `grep` por
	 * `CancellationPartners` não achava nenhum dos dois no dia em que a API mudasse a matriz.
	 */
	public static readonly CancellationPartners = [
		UserRoles.BrokerAdmin,
		UserRoles.BrokerUser,
		UserRoles.BrokerAgent,
	];
	/** Quem trabalha no parceiro. Mesma lista de `BROKER_ROLES` da API (Carteira do split). */
	public static readonly BrokerRoles = [UserRoles.BrokerAdmin, UserRoles.BrokerUser, UserRoles.BrokerAgent];
	/** Leitores são os solicitantes mais o parceiro externo somente leitura. */
	public static readonly CancellationReaders = [
		...UserRoles.CancellationRequesters,
		UserRoles.ExternalPartner,
	];
	/**
	 * Quem decide a pré-auditoria do contrato de locação (Aprovar / Retornar / Cancelar) e vê a fila
	 * em Produtos › Garantia Locatícia › Pré-auditoria. Mesma matriz dos endpoints `/pre-audits` e
	 * `/cases/{id}/pre-audit` da API: backoffice, sem exigir perfil de administrador.
	 */
	public static readonly PreAuditAnalysts = [UserRoles.Admin, UserRoles.User];
	/**
	 * Quem vê a aba "Pré-auditoria" no detalhe do contrato. Analistas veem os achados; os demais
	 * (parceiro e parceiro externo somente leitura) veem só o retorno, via `partner-view`.
	 */
	public static readonly PreAuditReaders = [
		...UserRoles.PreAuditAnalysts,
		...UserRoles.BrokerRoles,
		UserRoles.ExternalPartner,
	];
	/**
	 * Mesma matriz de `POLICY_RATING_READERS` da API: quem vê a letra do Rating no cabeçalho da proposta/contrato.
	 * Gerente e Representante Comercial ficam de fora de propósito — o endpoint não recorta por carteira, e eles só
	 * enxergam a própria.
	 */
	public static readonly PolicyRatingReaders = [
		UserRoles.Admin,
		UserRoles.ExternalPartner,
		UserRoles.BUSINESS_MANAGER,
	];
	/**
	 * Mesma matriz de `PolicyRatingListRoles` da API: quem recebe as colunas Rating/Pontos na lista de Propostas. Para os
	 * demais a API devolve os campos nulos.
	 */
	public static readonly PolicyRatingListReaders = [UserRoles.Admin, UserRoles.BUSINESS_MANAGER];
	/**
	 * Mesma matriz de `POLICY_RESULT_READERS` da API: quem vê o card "Cálculo de Rating" da Análise de Crédito (aba
	 * Atividades). O backoffice, o parceiro externo e o Diretor Comercial; Gerente e Representante ficam de fora pelo
	 * mesmo motivo do Rating do cabeçalho.
	 */
	public static readonly PolicyResultReaders = [
		UserRoles.Admin,
		UserRoles.User,
		UserRoles.ExternalPartner,
		UserRoles.BUSINESS_MANAGER,
	];
	public static readonly options = [
		{ label: 'Almada - Administrador', value: UserRoles.Admin },
		{ label: 'Parceiro Externo', value: UserRoles.ExternalPartner },
		{ label: 'Almada - Diretor Comercial', value: UserRoles.BUSINESS_MANAGER },
		{ label: 'Almada - Gerente Comercial', value: UserRoles.BUSINESS_HEAD },
		{ label: 'Almada - Representante Comercial', value: UserRoles.BUSINESS_CONSULTANT },
		{ label: 'Almada - Usuário', value: UserRoles.User },
		{ label: 'Parceiro - Administrador', value: UserRoles.BrokerAdmin },
		{ label: 'Parceiro - Usuário', value: UserRoles.BrokerUser },
		{ label: 'Parceiro - Corretor', value: UserRoles.BrokerAgent },
	];

	public static buildOptions(roles: string[]) {
		return UserRoles.options.filter((c) => roles.includes(c.value));
	}

	public static getDescription(role: string) {
		return UserRoles.options.find((c) => c.value == role)?.label || 'Não mapeado';
	}
}
